import React, { useState, useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  Tag,
  ChevronDown,
  ChevronUp,
  Filter,
  ArrowUpDown,
} from 'lucide-react';

interface AccountPerformance {
  accountId: string;
  accountName: string;
  networkId: string;
  platformName: string;
  ngr: number;
  deposits: number;
  withdrawals: number;
  ftds: number;
  signups: number;
  activePlayers: number;
  clicks: number;
  impressions: number;
  conversionRate: number;
  // Commission breakdown
  totalCommission: number;
  cpaCommission: number;
  revenueShareCommission: number;
  subAffiliateCommission: number;
  lastSyncAt: string | null;
  syncStatus: string;
  tags: string[];
}

interface Props {
  data: AccountPerformance[];
  isLoading?: boolean;
  currency?: string;
}

type SortField = 'deposits' | 'commission' | 'ftds' | 'activePlayers' | 'accountName';
type SortOrder = 'asc' | 'desc';

const INITIAL_DISPLAY_COUNT = 20;

const AccountPerformanceTable: React.FC<Props> = ({ data, isLoading, currency = 'EUR' }) => {
  const [sortField, setSortField] = useState<SortField>('commission');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [showAll, setShowAll] = useState(false);
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Get unique platforms for filter dropdown
  const platforms = useMemo(() => {
    if (!data) return [];
    const unique = [...new Set(data.map(a => a.platformName))].filter(Boolean);
    return unique.sort();
  }, [data]);

  // Filter and sort data
  const processedData = useMemo(() => {
    if (!data) return [];
    
    let filtered = [...data];
    
    // Apply platform filter
    if (platformFilter !== 'all') {
      filtered = filtered.filter(a => a.platformName === platformFilter);
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;
      
      switch (sortField) {
        case 'deposits':
          aVal = a.deposits;
          bVal = b.deposits;
          break;
        case 'commission':
          aVal = a.totalCommission || 0;
          bVal = b.totalCommission || 0;
          break;
        case 'ftds':
          aVal = a.ftds;
          bVal = b.ftds;
          break;
        case 'activePlayers':
          aVal = a.activePlayers;
          bVal = b.activePlayers;
          break;
        case 'accountName':
          aVal = a.accountName.toLowerCase();
          bVal = b.accountName.toLowerCase();
          break;
        default:
          aVal = a.deposits;
          bVal = b.deposits;
      }
      
      if (typeof aVal === 'string') {
        return sortOrder === 'asc' 
          ? aVal.localeCompare(bVal as string)
          : (bVal as string).localeCompare(aVal);
      }
      
      return sortOrder === 'asc' ? aVal - (bVal as number) : (bVal as number) - aVal;
    });
    
    return filtered;
  }, [data, sortField, sortOrder, platformFilter]);

  // Limit displayed data
  const displayedData = useMemo(() => {
    if (showAll) return processedData;
    return processedData.slice(0, INITIAL_DISPLAY_COUNT);
  }, [processedData, showAll]);

  const hasMore = processedData.length > INITIAL_DISPLAY_COUNT;
  const remainingCount = processedData.length - INITIAL_DISPLAY_COUNT;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatLastSync = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    return 'Just now';
  };

  const getSyncStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'failed':
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'running':
        return <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-zinc-500" />;
    }
  };

  const getCommissionIndicator = (commission: number) => {
    if (commission > 0) return <TrendingUp className="w-4 h-4 text-emerald-400" />;
    if (commission < 0) return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-zinc-500" />;
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 text-zinc-600" />;
    }
    return sortOrder === 'asc' 
      ? <ChevronUp className="w-3 h-3 text-emerald-400" />
      : <ChevronDown className="w-3 h-3 text-emerald-400" />;
  };

  if (isLoading) {
    return (
      <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,15%)] overflow-hidden">
        <div className="px-5 py-4 border-b border-[hsl(225,15%,15%)]">
          <h3 className="text-lg font-semibold text-white">Account Performance</h3>
          <p className="text-sm text-zinc-500 mt-1">Stats per affiliate account</p>
        </div>
        <div className="p-8 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 text-zinc-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,15%)] overflow-hidden">
        <div className="px-5 py-4 border-b border-[hsl(225,15%,15%)]">
          <h3 className="text-lg font-semibold text-white">Account Performance</h3>
          <p className="text-sm text-zinc-500 mt-1">Stats per affiliate account</p>
        </div>
        <div className="p-8 text-center">
          <p className="text-zinc-500">No accounts with statistics found</p>
          <p className="text-xs text-zinc-600 mt-2">Sync your accounts to see performance data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,15%)] overflow-hidden">
      {/* Header with controls */}
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-[hsl(225,15%,15%)]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-white">Account Performance</h3>
            <p className="text-xs sm:text-sm text-zinc-500 mt-0.5 sm:mt-1">
              {platformFilter !== 'all' ? `${processedData.length} of ${data.length}` : data.length} account(s)
              {!showAll && hasMore && ` • Top ${INITIAL_DISPLAY_COUNT}`}
            </p>
          </div>
          
          {/* Controls */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Sort dropdown */}
            <div className="relative flex-1 sm:flex-none">
              <select
                value={sortField}
                onChange={(e) => setSortField(e.target.value as SortField)}
                className="appearance-none w-full sm:w-auto bg-[hsl(225,15%,15%)] border border-[hsl(225,15%,20%)] rounded-lg px-3 py-1.5 pr-8 text-xs sm:text-sm text-zinc-300 focus:outline-none focus:border-emerald-500/50 cursor-pointer"
              >
                <option value="commission">Sort: Commission</option>
                <option value="deposits">Sort: Deposits</option>
                <option value="ftds">Sort: FTDs</option>
                <option value="activePlayers">Sort: Players</option>
                <option value="accountName">Sort: Name</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
            </div>
            
            {/* Order toggle */}
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-1.5 bg-[hsl(225,15%,15%)] border border-[hsl(225,15%,20%)] rounded-lg text-zinc-400 hover:text-white hover:border-emerald-500/50 transition-colors shrink-0"
              title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              {sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-1.5 border rounded-lg transition-colors shrink-0 ${
                showFilters || platformFilter !== 'all'
                  ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                  : 'bg-[hsl(225,15%,15%)] border-[hsl(225,15%,20%)] text-zinc-400 hover:text-white hover:border-emerald-500/50'
              }`}
              title="Filter"
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Filter panel */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t border-[hsl(225,15%,15%)] flex items-center gap-3">
            <span className="text-sm text-zinc-500">Platform:</span>
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="appearance-none bg-[hsl(225,15%,15%)] border border-[hsl(225,15%,20%)] rounded-lg px-3 py-1.5 pr-8 text-sm text-zinc-300 focus:outline-none focus:border-emerald-500/50 cursor-pointer"
            >
              <option value="all">All Platforms</option>
              {platforms.map(platform => (
                <option key={platform} value={platform}>{platform}</option>
              ))}
            </select>
            {platformFilter !== 'all' && (
              <button
                onClick={() => setPlatformFilter('all')}
                className="text-xs text-zinc-500 hover:text-white transition-colors"
              >
                Clear filter
              </button>
            )}
          </div>
        )}
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-[hsl(225,15%,15%)] bg-[hsl(225,15%,8%)]">
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('accountName')}
                  className="flex items-center gap-1 text-xs font-medium text-zinc-500 uppercase tracking-wider hover:text-zinc-300 transition-colors"
                >
                  Account <SortIcon field="accountName" />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                Platform
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('commission')}
                  className="flex items-center gap-1 justify-end w-full text-xs font-medium text-zinc-500 uppercase tracking-wider hover:text-zinc-300 transition-colors"
                >
                  Commission <SortIcon field="commission" />
                </button>
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                Rev Share
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                CPA
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('deposits')}
                  className="flex items-center gap-1 justify-end w-full text-xs font-medium text-zinc-500 uppercase tracking-wider hover:text-zinc-300 transition-colors"
                >
                  Deposits <SortIcon field="deposits" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('ftds')}
                  className="flex items-center gap-1 justify-end w-full text-xs font-medium text-zinc-500 uppercase tracking-wider hover:text-zinc-300 transition-colors"
                >
                  FTDs <SortIcon field="ftds" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('activePlayers')}
                  className="flex items-center gap-1 justify-end w-full text-xs font-medium text-zinc-500 uppercase tracking-wider hover:text-zinc-300 transition-colors"
                >
                  Players <SortIcon field="activePlayers" />
                </button>
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-zinc-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(225,15%,12%)]">
            {displayedData.map((account) => (
              <tr
                key={account.accountId}
                className="hover:bg-[hsl(225,15%,12%)] transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="font-medium text-white text-sm">
                        {account.accountName}
                      </div>
                      {account.tags && account.tags.length > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <Tag className="w-3 h-3 text-violet-400" />
                          {account.tags.map((tag, i) => (
                            <span
                              key={i}
                              className="px-1.5 py-0.5 bg-violet-500/20 text-violet-300 text-xs rounded"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-zinc-400">{account.platformName}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {getCommissionIndicator(account.totalCommission || 0)}
                    <span className={`font-mono font-medium text-sm ${
                      (account.totalCommission || 0) > 0 ? 'text-emerald-400' : 
                      (account.totalCommission || 0) < 0 ? 'text-red-400' : 'text-zinc-400'
                    }`}>
                      {formatCurrency(account.totalCommission || 0)}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-mono text-sm text-blue-400">
                    {formatCurrency(account.revenueShareCommission || 0)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-mono text-sm text-purple-400">
                    {formatCurrency(account.cpaCommission || 0)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-mono text-sm text-zinc-300">
                    {formatCurrency(account.deposits)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-mono text-sm text-blue-400">
                    {formatNumber(account.ftds)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-mono text-sm text-zinc-300">
                    {formatNumber(account.activePlayers)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    {getSyncStatusIcon(account.syncStatus)}
                    <span className="text-xs text-zinc-500">
                      {formatLastSync(account.lastSyncAt)}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Show More / Show Less */}
      {hasMore && (
        <div className="px-5 py-3 border-t border-[hsl(225,15%,15%)] text-center">
          <button
            onClick={() => setShowAll(!showAll)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-400 hover:text-emerald-300 transition-colors"
          >
            {showAll ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Show Less
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Show {remainingCount} More
              </>
            )}
          </button>
        </div>
      )}

      {/* Summary Footer */}
      <div className="px-4 sm:px-5 py-3 sm:py-4 border-t border-[hsl(225,15%,15%)] bg-[hsl(225,15%,8%)]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 text-xs sm:text-sm">
          <span className="text-zinc-500 shrink-0">
            {platformFilter !== 'all' ? 'Filtered Totals' : 'Totals'}
          </span>
          <div className="grid grid-cols-2 sm:flex sm:items-center gap-x-4 gap-y-1 sm:gap-6">
            <div className="text-right sm:text-right">
              <span className="text-zinc-500 mr-1 sm:mr-2">Commission:</span>
              <span className="font-mono font-semibold text-emerald-400">
                {formatCurrency(processedData.reduce((sum, a) => sum + (a.totalCommission || 0), 0))}
              </span>
            </div>
            <div className="text-right sm:text-right">
              <span className="text-zinc-500 mr-1 sm:mr-2">Rev Share:</span>
              <span className="font-mono font-semibold text-blue-400">
                {formatCurrency(processedData.reduce((sum, a) => sum + (a.revenueShareCommission || 0), 0))}
              </span>
            </div>
            <div className="text-right sm:text-right">
              <span className="text-zinc-500 mr-1 sm:mr-2">CPA:</span>
              <span className="font-mono font-semibold text-purple-400">
                {formatCurrency(processedData.reduce((sum, a) => sum + (a.cpaCommission || 0), 0))}
              </span>
            </div>
            <div className="text-right sm:text-right">
              <span className="text-zinc-500 mr-1 sm:mr-2">FTDs:</span>
              <span className="font-mono font-semibold text-zinc-300">
                {formatNumber(processedData.reduce((sum, a) => sum + a.ftds, 0))}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPerformanceTable;
